import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import { countryName } from '../lib/countries';
import { CURRENCIES } from '../lib/currency';
import { emptyEventData, emptyMeetupEventData } from '../lib/defaults';
import type { EventRecord, EventSummary, MeetupMeta } from '../lib/types';
import CityAutocomplete from './CityAutocomplete';
import CountrySelect from './CountrySelect';

type Props = {
  onClose: () => void;
  onCreated: (e: EventRecord) => void;
};

type Step = 'pick-type' | 'meetup-form';

export default function NewEventModal({ onClose, onCreated }: Props) {
  const [step, setStep] = useState<Step>('pick-type');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Close on Escape.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function createConference() {
    setBusy(true);
    setError(null);
    try {
      const ev = await api.createEvent({
        name: 'Untitled conference',
        event_type: 'conference',
        country_code: '',
        city: '',
        start_date: null,
        end_date: null,
        conference_days: 1,
        workshop_days: 0,
        currency: 'EUR',
        meetup_meta: {},
        data: emptyEventData(),
      });
      onCreated(ev);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-event-title"
        onClick={(e) => e.stopPropagation()}
      >
        {step === 'pick-type' ? (
          <TypePicker
            busy={busy}
            error={error}
            onConference={createConference}
            onMeetup={() => setStep('meetup-form')}
            onClose={onClose}
          />
        ) : (
          <MeetupForm
            onBack={() => setStep('pick-type')}
            onClose={onClose}
            onCreated={onCreated}
          />
        )}
      </div>
    </div>
  );
}

function TypePicker({
  busy,
  error,
  onConference,
  onMeetup,
  onClose,
}: {
  busy: boolean;
  error: string | null;
  onConference: () => void;
  onMeetup: () => void;
  onClose: () => void;
}) {
  return (
    <>
      <div className="modal-head">
        <h2 id="new-event-title">What kind of event?</h2>
        <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
      </div>

      <div className="modal-body">
        {error && <div className="error-banner mb-12">{error}</div>}
        <div className="type-picker">
          <button
            type="button"
            className="type-card"
            onClick={onConference}
            disabled={busy}
          >
            <div className="type-card-title">Conference</div>
            <div className="type-card-sub">
              Multi-day event with tickets, sponsors, add-ons, scenarios,
              break-even and cashflow timeline.
            </div>
            <div className="type-card-tag">Full template</div>
          </button>
          <button
            type="button"
            className="type-card"
            onClick={onMeetup}
            disabled={busy}
          >
            <div className="type-card-title">Meetup</div>
            <div className="type-card-sub">
              Recurring single-evening event. Track edition number, time,
              capacity and speakers.
            </div>
            <div className="type-card-tag">Lighter template</div>
          </button>
        </div>
      </div>

      <div className="modal-foot">
        <button className="btn ghost" onClick={onClose} disabled={busy}>
          Cancel
        </button>
      </div>
    </>
  );
}

type MeetupFormState = {
  name: string;
  number: number | '';
  countryCode: string;
  city: string;
  date: string;
  hour: string;
  maxParticipants: number | '';
  talkCount: number | '';
  currency: string;
};

function emptyMeetupForm(): MeetupFormState {
  return {
    name: '',
    number: '',
    countryCode: '',
    city: '',
    date: '',
    hour: '',
    maxParticipants: '',
    talkCount: '',
    currency: 'EUR',
  };
}

function MeetupForm({
  onBack,
  onClose,
  onCreated,
}: {
  onBack: () => void;
  onClose: () => void;
  onCreated: (e: EventRecord) => void;
}) {
  const [form, setForm] = useState<MeetupFormState>(emptyMeetupForm());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Copy-from-previous-meetup
  const [previousMeetups, setPreviousMeetups] = useState<EventSummary[] | null>(null);
  const [copyOpen, setCopyOpen] = useState(false);
  const [copiedFromId, setCopiedFromId] = useState<string | null>(null);
  // Hold the source record so we can reuse its `data` (cost lines, etc.)
  // when the user submits — fetched on demand.
  const [copiedFromRecord, setCopiedFromRecord] = useState<EventRecord | null>(null);

  useEffect(() => {
    if (!copyOpen || previousMeetups !== null) return;
    api
      .listEvents('active', 'meetup')
      .then(setPreviousMeetups)
      .catch(() => setPreviousMeetups([]));
  }, [copyOpen, previousMeetups]);

  // Sort previous meetups by date desc (then updated_at as tiebreaker).
  const sortedPrev = useMemo(() => {
    if (!previousMeetups) return null;
    return [...previousMeetups].sort((a, b) => {
      const ad = a.start_date ?? '';
      const bd = b.start_date ?? '';
      if (ad === bd) return b.updated_at.localeCompare(a.updated_at);
      return bd.localeCompare(ad);
    });
  }, [previousMeetups]);

  async function pickToCopy(s: EventSummary) {
    setError(null);
    try {
      const full = await api.getEvent(s.id);
      setCopiedFromId(s.id);
      setCopiedFromRecord(full);
      const m = (full.meetup_meta ?? {}) as MeetupMeta;
      setForm({
        // Prefill — but bump the edition number and clear the date so the
        // user can adjust to the upcoming event's specifics.
        name: full.name,
        number: typeof m.number === 'number' ? m.number + 1 : '',
        countryCode: full.country_code,
        city: full.city,
        date: '',
        hour: m.hour ?? '',
        maxParticipants: typeof m.max_participants === 'number' ? m.max_participants : '',
        talkCount: typeof m.talk_count === 'number' ? m.talk_count : '',
        currency: full.currency,
      });
      setCopyOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load source');
    }
  }

  function clearCopy() {
    setCopiedFromId(null);
    setCopiedFromRecord(null);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('Name is required.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const meetup_meta: MeetupMeta = {
        ...(form.number !== '' ? { number: Number(form.number) } : {}),
        ...(form.hour ? { hour: form.hour } : {}),
        ...(form.maxParticipants !== ''
          ? { max_participants: Number(form.maxParticipants) }
          : {}),
        ...(form.talkCount !== '' ? { talk_count: Number(form.talkCount) } : {}),
      };
      // If we copied from a previous meetup, carry its calculator state
      // (costs, sponsors-if-any, scenario, cashflow plans, VAT mode/rate);
      // otherwise start from the leaner meetup template (1 ticket tier,
      // 1 sponsor slot, no add-ons).
      const data = copiedFromRecord?.data ?? emptyMeetupEventData();
      const ev = await api.createEvent({
        name: form.name.trim(),
        event_type: 'meetup',
        country_code: form.countryCode,
        city: form.city,
        start_date: form.date || null,
        end_date: form.date || null, // meetup is single-day
        conference_days: 0,
        workshop_days: 0,
        currency: form.currency,
        meetup_meta,
        data,
      });
      onCreated(ev);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit}>
      <div className="modal-head">
        <h2 id="new-event-title">New meetup</h2>
        <button type="button" className="modal-close" onClick={onClose} aria-label="Close">×</button>
      </div>

      <div className="modal-body">
        {/* Copy-from-previous disclosure */}
        <div className="copy-disclosure">
          {copiedFromId ? (
            <div className="copy-active">
              <span className="muted">Prefilled from previous meetup.</span>
              <button type="button" className="btn sm ghost" onClick={clearCopy}>
                × Clear
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="btn ghost sm"
              onClick={() => setCopyOpen((v) => !v)}
            >
              {copyOpen ? '▴ Hide previous meetups' : '▾ Copy from previous meetup'}
            </button>
          )}

          {copyOpen && !copiedFromId && (
            <div className="copy-list">
              {sortedPrev === null ? (
                <div className="muted">Loading…</div>
              ) : sortedPrev.length === 0 ? (
                <div className="muted">No previous meetups yet.</div>
              ) : (
                sortedPrev.map((m) => (
                  <button
                    type="button"
                    key={m.id}
                    className="copy-list-item"
                    onClick={() => pickToCopy(m)}
                  >
                    <span className="copy-item-name">{m.name || 'Untitled'}</span>
                    <span className="copy-item-meta">
                      {m.start_date ?? 'no date'} ·{' '}
                      {[m.city, m.country_code ? countryName(m.country_code) : ''].filter(Boolean).join(', ')}
                    </span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {error && <div className="error-banner mt-12">{error}</div>}

        <div className="meetup-form-grid">
          <div className="field col-span-full">
            <label>Meetup name</label>
            <input
              className="input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Cloud Native Warsaw"
              autoFocus
              required
            />
          </div>

          <div className="field">
            <label>Edition (#)</label>
            <input
              className="input"
              type="number"
              min={1}
              value={form.number}
              onChange={(e) =>
                setForm({ ...form, number: e.target.value === '' ? '' : Number(e.target.value) })
              }
              placeholder="12"
            />
          </div>
          <div className="field">
            <label>Date</label>
            <input
              className="input"
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
            />
          </div>
          <div className="field">
            <label>Start time</label>
            <input
              className="input"
              type="time"
              value={form.hour}
              onChange={(e) => setForm({ ...form, hour: e.target.value })}
            />
          </div>

          <div className="field">
            <label>Country</label>
            <CountrySelect
              value={form.countryCode}
              onChange={(code) => setForm({ ...form, countryCode: code })}
            />
          </div>
          <div className="field col-span-2">
            <label>City</label>
            <CityAutocomplete
              value={form.city}
              countryCode={form.countryCode}
              onChangeText={(city) => setForm({ ...form, city })}
              onPick={(city, code) =>
                setForm({ ...form, city, countryCode: code || form.countryCode })
              }
            />
          </div>

          <div className="field">
            <label>Max participants</label>
            <input
              className="input"
              type="number"
              min={0}
              value={form.maxParticipants}
              onChange={(e) =>
                setForm({
                  ...form,
                  maxParticipants: e.target.value === '' ? '' : Number(e.target.value),
                })
              }
              placeholder="80"
            />
          </div>
          <div className="field">
            <label>Talks / speakers</label>
            <input
              className="input"
              type="number"
              min={0}
              value={form.talkCount}
              onChange={(e) =>
                setForm({
                  ...form,
                  talkCount: e.target.value === '' ? '' : Number(e.target.value),
                })
              }
              placeholder="3"
            />
          </div>
          <div className="field">
            <label>Currency</label>
            <select
              className="input"
              value={form.currency}
              onChange={(e) => setForm({ ...form, currency: e.target.value })}
            >
              {CURRENCIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.code} — {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="modal-foot">
        <button type="button" className="btn ghost" onClick={onBack} disabled={busy}>
          ← Back
        </button>
        <button type="submit" className="btn" disabled={busy}>
          {busy ? 'Creating…' : 'Create meetup'}
        </button>
      </div>
    </form>
  );
}
